/**
 * FESTIVOS DE UN CENTRO — FASE 6.3 §B/§C/§D/§F
 *
 * Calendario anual interactivo sobre `/api/centros/:id/calendario`. Click
 * en un día vacío → crearlo; click en un festivo → verlo y, si se puede
 * editar, quitarlo. Nunca un modal por click (eso es sólo para «Aplicar
 * plantilla», que sí es una acción deliberada de varios pasos).
 */
import { useState, type FormEvent, type ReactNode } from 'react';

import { api, type Calendario, type DiaPlantilla, type ExcepcionCalendario, type PlantillaCalendario } from '../api.ts';
import { CalendarioAnual, type DiaCalendario } from './calendario-anual.tsx';
import { Error as ErrorCaja, Modal, Panel, useDatos } from './base.tsx';

export function FestivosCentro({ idCentro, puedeEditar }: { idCentro: string; puedeEditar: boolean }): ReactNode {
  const [anio, setAnio] = useState(() => new Date().getFullYear());
  const calendario = useDatos(() => api.get<Calendario>(`/api/centros/${idCentro}/calendario`), [idCentro]);
  const [seleccionado, setSeleccionado] = useState<string | null>(null);
  const [mostrarAplicar, setMostrarAplicar] = useState(false);
  const [mensaje, setMensaje] = useState<string | null>(null);

  function alClickDia(fecha: string, excepciones: readonly ExcepcionCalendario[]): void {
    const existe = excepciones.some((e) => e.fecha === fecha);
    //  ENCARGADO (sólo lectura): un día vacío no tiene nada que mostrar,
    //  así que ni se selecciona — evita que aparezca un panel de creación
    //  que de todos modos no podría enviar nada.
    if (!puedeEditar && !existe) return;
    setSeleccionado((s) => (s === fecha ? null : fecha));
  }

  function alCambiar(msg: string): void {
    setMensaje(msg);
    setSeleccionado(null);
    calendario.recargar();
  }

  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Festivos</h2>
        <div className="fila">
          <button type="button" className="pequeno" onClick={() => setAnio((a) => a - 1)} aria-label="Año anterior">
            ◀ {anio - 1}
          </button>
          <strong>{anio}</strong>
          <button type="button" className="pequeno" onClick={() => setAnio((a) => a + 1)} aria-label="Año siguiente">
            {anio + 1} ▶
          </button>
          {puedeEditar && (
            <button type="button" className="pequeno" onClick={() => setMostrarAplicar(true)}>
              Aplicar plantilla
            </button>
          )}
        </div>
      </div>
      <div className="panel-cuerpo">
        {mensaje !== null && (
          <div className="nota exito" role="status" style={{ marginBottom: 12 }}>
            {mensaje}
          </div>
        )}
        <Panel estado={calendario}>
          {(cal) => {
            const dias = new Map<string, DiaCalendario>();
            for (const ex of cal.excepciones) {
              if (!ex.fecha.startsWith(String(anio))) continue;
              dias.set(ex.fecha, {
                fecha: ex.fecha,
                descripcion: ex.descripcion,
                tipo: ex.tipo,
                origen: ex.origen,
                soloLectura: ex.nacional,
              });
            }
            return (
              <>
                <CalendarioAnual
                  anio={anio}
                  dias={dias}
                  seleccionado={seleccionado}
                  onClickDia={(f) => alClickDia(f, cal.excepciones)}
                />
                {seleccionado !== null && (
                  <PanelDiaFestivo
                    idCentro={idCentro}
                    fecha={seleccionado}
                    existente={cal.excepciones.find((e) => e.fecha === seleccionado) ?? null}
                    puedeEditar={puedeEditar}
                    onCerrar={() => setSeleccionado(null)}
                    onCreado={() => alCambiar('Festivo añadido.')}
                    onBorrado={() => alCambiar('Festivo eliminado.')}
                  />
                )}
              </>
            );
          }}
        </Panel>
      </div>
      {mostrarAplicar && (
        <AplicarPlantilla
          idCentro={idCentro}
          excepcionesActuales={calendario.datos?.excepciones ?? []}
          onCerrar={() => setMostrarAplicar(false)}
          onAplicada={(msg) => {
            setMostrarAplicar(false);
            alCambiar(msg);
          }}
        />
      )}
    </div>
  );
}

const ETIQUETA_DIA_SEMANA = new Intl.DateTimeFormat('es-ES', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });

function fechaLarga(iso: string): string {
  return ETIQUETA_DIA_SEMANA.format(new Date(`${iso}T00:00:00Z`));
}

function PanelDiaFestivo({
  idCentro,
  fecha,
  existente,
  puedeEditar,
  onCerrar,
  onCreado,
  onBorrado,
}: {
  idCentro: string;
  fecha: string;
  existente: ExcepcionCalendario | null;
  puedeEditar: boolean;
  onCerrar: () => void;
  onCreado: () => void;
  onBorrado: () => void;
}): ReactNode {
  if (existente !== null) {
    return (
      <ExisteFestivo idCentro={idCentro} excepcion={existente} puedeEditar={puedeEditar} onCerrar={onCerrar} onBorrado={onBorrado} />
    );
  }
  if (!puedeEditar) return null;
  return <CrearFestivo idCentro={idCentro} fecha={fecha} onCerrar={onCerrar} onCreado={onCreado} />;
}

function ExisteFestivo({
  idCentro,
  excepcion,
  puedeEditar,
  onCerrar,
  onBorrado,
}: {
  idCentro: string;
  excepcion: ExcepcionCalendario;
  puedeEditar: boolean;
  onCerrar: () => void;
  onBorrado: () => void;
}): ReactNode {
  const [confirmando, setConfirmando] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function borrar(): Promise<void> {
    setBorrando(true);
    setError(null);
    try {
      await api.del(`/api/centros/${idCentro}/calendario/${excepcion.id}`);
      onBorrado();
    } catch (err) {
      setError(err);
      setBorrando(false);
    }
  }

  return (
    <div className="calendario-detalle">
      <div className="fila separa">
        <div>
          <div className="fecha-detalle">{fechaLarga(excepcion.fecha)}</div>
          <div className="suave pequena">{excepcion.descripcion ?? '—'}</div>
        </div>
        <span className={`etiqueta ${excepcion.nacional ? 'nacional' : excepcion.origen === 'PLANTILLA' ? 'propia' : 'neutra'}`}>
          {excepcion.nacional ? 'NACIONAL' : excepcion.origen === 'PLANTILLA' ? 'PLANTILLA' : 'MANUAL'}
        </span>
      </div>
      <p className="pequena tenue" style={{ marginTop: 6 }}>
        {excepcion.tipo === 'OPERATIVO_EXTRAORDINARIO' ? 'Día operativo extraordinario.' : 'Día no operativo (festivo).'}
      </p>
      {error !== null && <ErrorCaja error={error} />}
      <div className="fila" style={{ marginTop: 10 }}>
        {excepcion.nacional ? (
          <span className="pequena tenue">Festivo nacional — no se puede quitar desde un centro.</span>
        ) : !puedeEditar ? null : confirmando ? (
          <>
            <button type="button" className="pequeno" onClick={() => { void borrar(); }} disabled={borrando}>
              {borrando ? 'Quitando…' : 'Confirmar'}
            </button>
            <button type="button" className="pequeno" onClick={() => setConfirmando(false)} disabled={borrando}>
              Cancelar
            </button>
          </>
        ) : (
          <button type="button" className="pequeno" onClick={() => setConfirmando(true)}>
            Quitar festivo
          </button>
        )}
        <button type="button" className="pequeno enlace" onClick={onCerrar} style={{ marginLeft: 'auto' }}>
          Cerrar
        </button>
      </div>
    </div>
  );
}

function CrearFestivo({
  idCentro,
  fecha,
  onCerrar,
  onCreado,
}: {
  idCentro: string;
  fecha: string;
  onCerrar: () => void;
  onCreado: () => void;
}): ReactNode {
  const [descripcion, setDescripcion] = useState('');
  const [tipo, setTipo] = useState<'NO_OPERATIVO' | 'OPERATIVO_EXTRAORDINARIO'>('NO_OPERATIVO');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function enviar(e: FormEvent): Promise<void> {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post<ExcepcionCalendario>(`/api/centros/${idCentro}/calendario`, {
        fecha,
        tipo,
        //  La descripción es obligatoria en el modelo (0000: NOT NULL); en
        //  la UI es opcional de verdad — si se deja en blanco, se manda un
        //  valor por defecto legible en vez de bloquear el guardado.
        descripcion: descripcion.trim() || (tipo === 'NO_OPERATIVO' ? 'Festivo' : 'Día operativo extraordinario'),
      });
      onCreado();
    } catch (err) {
      setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="calendario-detalle" onSubmit={(e) => { void enviar(e); }}>
      <div className="fecha-detalle">{fechaLarga(fecha)}</div>
      <p className="pequena tenue">Este día todavía no tiene ninguna excepción.</p>
      <div className="fila" style={{ marginTop: 8, marginBottom: 8 }}>
        <label className="fila" style={{ gap: 4, fontWeight: 'normal' }}>
          <input type="radio" name="tipo-festivo" checked={tipo === 'NO_OPERATIVO'} onChange={() => setTipo('NO_OPERATIVO')} disabled={enviando} />
          Festivo
        </label>
        <label className="fila" style={{ gap: 4, fontWeight: 'normal' }}>
          <input
            type="radio"
            name="tipo-festivo"
            checked={tipo === 'OPERATIVO_EXTRAORDINARIO'}
            onChange={() => setTipo('OPERATIVO_EXTRAORDINARIO')}
            disabled={enviando}
          />
          Operativo extraordinario
        </label>
      </div>
      <div className="campo" style={{ marginBottom: 8 }}>
        <label htmlFor="descripcion-festivo">Descripción (opcional)</label>
        <input
          id="descripcion-festivo"
          type="text"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          disabled={enviando}
          placeholder={tipo === 'NO_OPERATIVO' ? 'Festivo' : 'Día operativo extraordinario'}
        />
      </div>
      {error !== null && <ErrorCaja error={error} />}
      <div className="fila">
        <button type="submit" className="principal pequeno" disabled={enviando}>
          {enviando ? 'Guardando…' : 'Marcar festivo'}
        </button>
        <button type="button" className="pequeno" onClick={onCerrar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

// ------------------------------------------------------- aplicar plantilla

function AplicarPlantilla({
  idCentro,
  excepcionesActuales,
  onCerrar,
  onAplicada,
}: {
  idCentro: string;
  excepcionesActuales: readonly ExcepcionCalendario[];
  onCerrar: () => void;
  onAplicada: (mensaje: string) => void;
}): ReactNode {
  const plantillas = useDatos(() => api.get<{ plantillas: PlantillaCalendario[] }>('/api/plantillas-calendario'), []);
  const [idPlantilla, setIdPlantilla] = useState<string | null>(null);
  const dias = useDatos(
    () => (idPlantilla === null ? Promise.resolve({ dias: [] as DiaPlantilla[] }) : api.get<{ dias: DiaPlantilla[] }>(`/api/plantillas-calendario/${idPlantilla}/dias`)),
    [idPlantilla],
  );
  const [aplicando, setAplicando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const fechasActuales = new Set(excepcionesActuales.map((e) => e.fecha));
  const diasPlantilla = dias.datos?.dias ?? [];
  const nuevos = diasPlantilla.filter((d) => !fechasActuales.has(d.fecha));
  const yaExisten = diasPlantilla.filter((d) => fechasActuales.has(d.fecha));

  async function aplicar(): Promise<void> {
    if (idPlantilla === null) return;
    setAplicando(true);
    setError(null);
    try {
      const r = await api.post<{ diasCopiados: number }>(`/api/centros/${idCentro}/calendario/aplicar-plantilla`, { idPlantilla });
      const copiados = r.diasCopiados === 1 ? 'Se añadió 1 festivo.' : `Se añadieron ${String(r.diasCopiados)} festivos.`;
      const existian =
        yaExisten.length === 0
          ? ''
          : yaExisten.length === 1
            ? ' 1 ya existía y se mantuvo.'
            : ` ${String(yaExisten.length)} ya existían y se mantuvieron.`;
      onAplicada(copiados + existian);
    } catch (err) {
      setError(err);
    } finally {
      setAplicando(false);
    }
  }

  return (
    <Modal titulo="Aplicar plantilla de festivos" cerrar={onCerrar}>
      <Panel estado={plantillas}>
        {(p) =>
          p.plantillas.length === 0 ? (
            <p className="suave">Todavía no hay ninguna plantilla de festivos creada.</p>
          ) : (
            <div className="campo">
              <label htmlFor="select-plantilla">Plantilla</label>
              <select id="select-plantilla" value={idPlantilla ?? ''} onChange={(e) => setIdPlantilla(e.target.value || null)}>
                <option value="">Elige una plantilla…</option>
                {p.plantillas.map((pl) => (
                  <option key={pl.id} value={pl.id}>
                    {pl.nombre} ({pl.dias} días)
                  </option>
                ))}
              </select>
            </div>
          )
        }
      </Panel>

      {idPlantilla !== null && (
        <Panel estado={dias}>
          {() => (
            <div style={{ marginTop: 10 }}>
              <p className="pequena">
                <strong>{nuevos.length}</strong> día{nuevos.length === 1 ? '' : 's'} se añadirá
                {nuevos.length === 1 ? '' : 'n'} al calendario del centro.
                {yaExisten.length > 0 && (
                  <>
                    {' '}
                    <strong>{yaExisten.length}</strong> ya existe{yaExisten.length === 1 ? '' : 'n'} y se mantendrá
                    {yaExisten.length === 1 ? '' : 'n'} tal cual.
                  </>
                )}
              </p>
              {nuevos.length > 0 && (
                <ul className="pequena suave" style={{ margin: 0, paddingLeft: 18, maxHeight: 160, overflowY: 'auto' }}>
                  {nuevos.map((d) => (
                    <li key={d.id}>
                      {d.fecha} — {d.descripcion}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </Panel>
      )}

      {error !== null && <ErrorCaja error={error} />}
      <div className="fila-botones" style={{ marginTop: 16 }}>
        <button type="button" className="principal" onClick={() => { void aplicar(); }} disabled={idPlantilla === null || aplicando}>
          {aplicando ? 'Aplicando…' : 'Aplicar al centro'}
        </button>
        <button type="button" onClick={onCerrar} disabled={aplicando}>
          Cancelar
        </button>
      </div>
    </Modal>
  );
}
